import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';

/**
 * The filter pill above a list: `Alle (12)`, `Offen (3)`, optionally with an icon on the left.
 *
 * Controlled: it shows `active` and reports a click, it never selects itself — the host decides
 * which pill is on. Text and count arrive ready to show; nothing is translated here.
 */
@Component({
    selector: 'ham-filter-pill',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `<button
        type="button"
        class="ham-filter-pill"
        [class.active]="active"
        [class.is-disabled]="disabled"
        [attr.aria-pressed]="active"
        [attr.aria-disabled]="disabled ? 'true' : null"
        (click)="onClick()"
    >
        <lucide-icon
            *ngIf="icon"
            class="ham-filter-pill__icon"
            [name]="icon"
            [size]="14"
            aria-hidden="true"
        ></lucide-icon>
        <span class="ham-filter-pill__text"
            >{{ label
            }}<ng-container *ngIf="count !== null">
                (<span data-testid="filter-pill-count">{{ count }}</span
                >)</ng-container
            ></span
        >
    </button>`,
    styleUrls: ['./ham-filter-pill.component.scss'],
})
export class HamFilterPillComponent {
    @Input() label = '';
    /** Shown as `(n)` after the label; `null` shows no count at all. */
    @Input() count: number | null = null;
    /** A Lucide icon name, e.g. `bot-message-square`. */
    @Input() icon: string | null = null;
    @Input() active = false;
    /**
     * Not available right now. Not the native `disabled`: that button swallows the pointer, and the
     * host's tooltip is what says why. The pill reports no click while it is set.
     */
    @Input() disabled = false;
    @Output() pillClick = new EventEmitter<void>();

    public onClick(): void {
        if (!this.disabled) {
            this.pillClick.emit();
        }
    }

    /** On the host, so a test finds the state where it finds the pill's own test id. */
    @HostBinding('attr.data-active') get dataActive(): string {
        return String(this.active);
    }
}
